import type { RouteLocationRaw } from 'vue-router'
import type { LabelColor } from '@/services/domain/label/types'
import type ContactListFolderSummary from '@/data/models/domain/contactGrouping/ContactListFolderSummary'
import type ContactListFolderTreeNode from '@/data/models/domain/contactGrouping/ContactListFolderTreeNode'
import type { ModelRaw } from '@/types'

export type ContactListFolderBody = {
  name: string
  color: LabelColor
}

export type ContactListFolderCreateBody = ContactListFolderBody & {
  folderId: number | null
}

export type ContactListFolderSummaryResponse = Omit<ModelRaw<ContactListFolderSummary>, 'id' | 'breadcrumbIds'>

export type ContactGroupingItemsVisibilityParams = {
  folderIds: string[]
  listIds: string[]
  all: boolean
}

export type ContactGroupingItemsVisibilityBody = {
  folderIds: number[]
  listIds: number[]
  all: boolean
}

export type ContactListFolderTreeNodeResponse = Pick<
  ModelRaw<ContactListFolderTreeNode>,
  'folder' | 'depth' | 'canMoveHere'
>

export type ContactListFolderTreeResponse = {
  items: ContactListFolderTreeNodeResponse[]
}

export type ContactListFolderTreeParams = {
  folderIds?: string[]
  all?: boolean
  sourceFolderId?: string | null
}

export type ContactListFolderTreeItem = {
  node: ContactListFolderTreeNode
  children: ContactListFolderTreeItem[]
}

export type ContactGroupingItemsMoveParams = {
  targetFolderId: string | null
  folderIds: string[]
  listIds: string[]
  all: boolean
  sourceFolderId: string | null
}

export type ContactGroupingItemsMoveBody = {
  targetFolderId: number | string | null
  folderIds?: number[]
  listIds?: number[]
  all?: boolean
  sourceFolderId?: string | null
  onlyVisible: boolean
}

export enum FolderDeletionStrategy {
  FoldersOnly = 'folders_only',
  WithLists = 'with_lists',
  WithListsAndContacts = 'with_lists_and_contacts',
}

export type ContactGroupingItemsDeleteSelection = {
  folderIds: string[]
  listIds: string[]
  /** Takes the whole level instead of the listed ids, which the backend then ignores */
  all: boolean
  sourceFolderId: string | null
}

export type ContactGroupingItemsDeletePreviewQuery = {
  folderIds?: string
  listIds?: string
  all?: 1
  sourceFolderId?: string
  onlyVisible: 1
}

export type ContactGroupingItemsDeletePreview = {
  /** Folders the FoldersOnly strategy would delete */
  selectedFolders: number
  /** Folders the other two strategies would delete, subtree included */
  subtreeFolders: number
  lists: number
  /** Lists kept because an automation or a subscribe widget points at them */
  blockedLists: number
  /** Shared lists of other users, which only lose their place in the folder */
  foreignLists: number
}

export type ContactGroupingItemsDeleteBody = {
  strategy: FolderDeletionStrategy
  folderIds?: number[]
  listIds?: number[]
  all?: boolean
  sourceFolderId?: string | null
  onlyVisible: boolean
}

export type ContactGroupingItemsDeleteResult = {
  deletedFolders: number
  deletedLists: number
  blockedListIds: number[]
}

export type ContactGroupingItemsDeleteModalParams = ContactGroupingItemsDeleteSelection & {
  selectedCount: number
  singleFolderName: string | null
  onSuccess?: () => void
}

// select-all keeps the ids of the current page only, so a longer level hides part of the selection
export const isDeleteSelectionKnown = ({
  all,
  folderIds,
  listIds,
  selectedCount,
}: ContactGroupingItemsDeleteModalParams) => !all || folderIds.length + listIds.length === selectedCount

/** Name of the wording set each strategy has under `options`, `descriptions`, `buttons` and `successNotification` */
export const deleteStrategyCopyKeys = {
  [FolderDeletionStrategy.FoldersOnly]: 'foldersOnly',
  [FolderDeletionStrategy.WithLists]: 'withLists',
  [FolderDeletionStrategy.WithListsAndContacts]: 'withListsAndContacts',
} as const

/** `lists` belongs to the list-only delete, which has no modal and therefore only a `successNotification` group */
export type DeleteCopyVariant = 'folder' | 'folders' | 'items' | 'lists'

/**
 * Name of the wording set the copy asks for: one named folder, folders only, or anything else.
 * The translations keep a group per name under `title`, `options`, `descriptions`, `buttons` and `successNotification`.
 */
export const getDeleteCopyVariant = (
  params: ContactGroupingItemsDeleteModalParams,
): Exclude<DeleteCopyVariant, 'lists'> => {
  if (params.singleFolderName) {
    return 'folder'
  }

  if (isDeleteSelectionKnown(params) && !params.listIds.length) {
    return 'folders'
  }

  return 'items'
}

export type ContactListFolderSummaryResolverParams = {
  requestParam: string
  redirectToIfNotFound?: RouteLocationRaw
}

export const CONTACT_LIST_FOLDER_NAME_MAX_LENGTH = 100
export const CONTACT_LIST_FOLDER_MAX_DEPTH = 5
